/**
 * Wallet API Service
 * Handles all wallet-related API calls (balance, transactions, deposits, ticket payments)
 */

import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';

// Create axios instance with base configuration
const walletAPI = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 60000
});

// Add token to all requests
walletAPI.interceptors.request.use(config => {
  // Try to get token from various storage locations in order of preference
  let token = null;

  // 1. Try direct token keys
  token =
    localStorage.getItem('token') ||
    sessionStorage.getItem('token') ||
    localStorage.getItem('authToken') ||
    sessionStorage.getItem('authToken') ||
    localStorage.getItem('jwt_token') ||
    sessionStorage.getItem('jwt_token');

  // 2. If not found, try to extract from authInfo object
  if (!token) {
    try {
      const authInfoStr = localStorage.getItem('authInfo');
      if (authInfoStr) {
        const authInfo = JSON.parse(authInfoStr);
        if (authInfo.token) {
          token = authInfo.token;
          console.log('[WalletAPI] Token extracted from authInfo');
        }
      }
    } catch (e) {
      console.warn('[WalletAPI] Could not parse authInfo:', e);
    }
  }

  // 3. Also check sessionStorage authInfo
  if (!token) {
    try {
      const authInfoStr = sessionStorage.getItem('authInfo');
      if (authInfoStr) {
        const authInfo = JSON.parse(authInfoStr);
        if (authInfo.token) {
          token = authInfo.token;
          console.log('[WalletAPI] Token extracted from sessionStorage authInfo');
        }
      }
    } catch (e) {
      console.warn('[WalletAPI] Could not parse sessionStorage authInfo:', e);
    }
  }

  if (token) {
    // Ensure token is properly formatted
    const bearerToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    config.headers.Authorization = bearerToken;
    console.log('[WalletAPI] Token added to request headers ✓');
  } else {
    console.warn('[WalletAPI] No token found in storage. Will get 401 Unauthorized.');
  }

  return config;
});

// Handle 401 responses
walletAPI.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      console.warn('Unauthorized (401): Token may be expired or invalid. Check localStorage/sessionStorage for valid token.');
      // Optionally redirect to login
      // window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Wallet Balance Management
 */
export const WalletBalanceAPI = {
  /**
   * Get current wallet balance
   * @returns {Promise<Object>} Balance information
   */
  getBalance: async () => {
    try {
      const response = await walletAPI.get('/api/finance/dashboard');
      return response.data?.data?.metrics || {};
    } catch (error) {
      console.error('Error fetching wallet balance:', error);
      // Fallback to v1 endpoint
      try {
        const fallback = await walletAPI.get('/v1/wallet/balance');
        return fallback.data;
      } catch (fallbackError) {
        throw new Error('Unable to fetch wallet balance');
      }
    }
  },

  /**
   * Get wallet balance by organization ID
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Balance information
   */
  getBalanceByOrg: async (organizationId) => {
    try {
      const response = await walletAPI.get('/api/finance/dashboard', {
        params: { organizationId }
      });
      return response.data?.data?.metrics || {};
    } catch (error) {
      console.error('Error fetching wallet balance for org:', error);
      throw error;
    }
  }
};

/**
 * Wallet Transactions
 */
export const WalletTransactionAPI = {
  /**
   * Get wallet transactions
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} List of transactions
   */
  getTransactions: async (params = {}) => {
    try {
      const response = await walletAPI.get('/api/finance/reports/finalized', { params });
      return response.data?.data?.reports || [];
    } catch (error) {
      console.error('Error fetching wallet transactions:', error);
      // Fallback to v1 endpoint
      try {
        const fallback = await walletAPI.get('/v1/wallet/transactions', { params });
        return fallback.data;
      } catch (fallbackError) {
        throw new Error('Unable to fetch transactions');
      }
    }
  },

  /**
   * Get transaction history for a date range
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {Promise<Array>} List of transactions
   */
  getTransactionsByDateRange: async (startDate, endDate) => {
    return WalletTransactionAPI.getTransactions({
      startDate,
      endDate
    });
  },

  /**
   * Get all wallet transactions (CREDIT and DEBIT) for current user
   * @param {Object} params - Query parameters (page, size, includeAll)
   * @returns {Promise<Array>} List of wallet transactions with type, amount, description, date
   */
  getWalletTransactions: async (params = {}) => {
    try {
      console.log('[WalletTransactionAPI] Fetching wallet transactions from /v1/wallet/transactions/transactions');
      // If includeAll is true, don't filter by status to get PENDING transactions
      const queryParams = { ...params };
      if (queryParams.includeAll) {
        delete queryParams.includeAll; // Remove this custom param before sending to API
        // Don't add status filter - let backend return all statuses
      }
      const response = await walletAPI.get('/v1/wallet/transactions/transactions', { params: queryParams });
      console.log('[WalletTransactionAPI] Wallet transactions response:', response.data);
      // The endpoint returns an array of transaction DTOs directly
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('[WalletTransactionAPI] Error fetching wallet transactions:', error);
      throw error;
    }
  }
};

/**
 * Wallet Deposit/Topup
 */
export const WalletDepositAPI = {
  /**
   * Submit a wallet deposit request
   * @param {Object} depositData - Deposit information
   * @returns {Promise<Object>} Deposit confirmation
   */
  createDeposit: async (depositData) => {
    try {
      const response = await walletAPI.post('/api/wallet/deposit', depositData);
      return response.data;
    } catch (error) {
      console.error('Error creating deposit:', error);
      // Fallback to v1 endpoint
      try {
        const fallback = await walletAPI.post('/v1/wallet/deposit', depositData);
        return fallback.data;
      } catch (fallbackError) {
        throw new Error('Unable to create deposit');
      }
    }
  },

  /**
   * Create an online topup request via a payment gateway
   * @param {Object} topupData - { amount }
   * @param {string} gateway - Payment gateway name (e.g., 'NGENIUS', 'STRIPE')
   * @returns {Promise<Object>} Response with paymentUrl for redirect
   */
  createOnlineTopup: async (topupData, gateway = 'NGENIUS') => {
    try {
      const response = await walletAPI.post('/v1/topupRequest', {
        ...topupData,
        paymentGateway: gateway
      });
      return response.data;
    } catch (error) {
      console.error('[WalletDepositAPI] Error creating online topup:', error);
      throw error;
    }
  },

  /**
   * Get pending deposit requests
   * @returns {Promise<Array>} List of pending deposits
   */
  getPendingDeposits: async () => {
    try {
      const response = await walletAPI.get('/api/finance/pending-approvals');
      return response.data?.data?.pendingApprovalsCount || 0;
    } catch (error) {
      console.error('Error fetching pending deposits:', error);
      throw error;
    }
  },

  /**
   * Approve a deposit request
   * @param {string} depositId - Deposit ID
   * @returns {Promise<Object>} Approval confirmation
   */
  approveDeposit: async (depositId) => {
    try {
      const response = await walletAPI.post(`/api/wallet/deposit/${depositId}/approve`);
      return response.data;
    } catch (error) {
      console.error('Error approving deposit:', error);
      throw error;
    }
  }
};

/**
 * Ticket Payment
 */
export const TicketPaymentAPI = {
  /**
   * Get unpaid tickets for customer
   * @param {string} customerId - Customer organization ID (required)
   * @returns {Promise<Array>} List of unpaid tickets
   */
  getUnpaidTickets: async (customerId = '') => {
    try {
      // If no customerId provided, use the organization from auth info
      let customerIdToUse = customerId;
      if (!customerIdToUse) {
        try {
          const authInfo = JSON.parse(localStorage.getItem('authInfo') || '{}');
          customerIdToUse = authInfo.principalInfo?.organisationId;
          console.log('[TicketPaymentAPI] Using organisation from auth:', customerIdToUse);
        } catch (e) {
          console.error('[TicketPaymentAPI] Could not extract organisation from auth');
        }
      }

      if (!customerIdToUse) {
        console.error('[TicketPaymentAPI] No customerId provided and could not extract from auth');
        return [];
      }

      // Call the correct endpoint: /api/v1/tickets/{customerId}/unpaid
      const response = await walletAPI.get(`/api/v1/tickets/${customerIdToUse}/unpaid`);
      console.log('[TicketPaymentAPI] Unpaid tickets response:', response.data);

      // Handle both direct array response and wrapped response
      const data = Array.isArray(response.data) ? response.data : (response.data?.data || response.data?.list || []);
      return data;
    } catch (error) {
      console.error('[TicketPaymentAPI] Error fetching unpaid tickets:', error);
      throw error;
    }
  },

  /**
   * Pay for selected tickets using wallet
   * @param {Object} paymentData - Payment information
   * @returns {Promise<Object>} Payment confirmation
   */
  payForTickets: async (paymentData) => {
    try {
      const response = await walletAPI.post('/api/wallet/debit-for-ticket-payment', paymentData);
      return response.data;
    } catch (error) {
      console.error('Error paying for tickets:', error);
      throw error;
    }
  },

  /**
   * Submit online payment for tickets via a payment gateway
   * @param {string} customerId - Customer organization ID
   * @param {Object} paymentData - { ticketIds: [uuid, ...] }
   * @param {string} gateway - Payment gateway name (e.g., 'NGENIUS', 'STRIPE')
   * @returns {Promise<Object>} Response with paymentUrl for redirect
   */
  submitOnlinePayment: async (customerId, paymentData, gateway = 'NGENIUS') => {
    try {
      const response = await walletAPI.post(`/api/v1/tickets/${customerId}/online-payment`, {
        ...paymentData,
        paymentGateway: gateway
      });
      return response.data;
    } catch (error) {
      console.error('[TicketPaymentAPI] Error submitting online payment:', error);
      throw error;
    }
  },

  /**
   * Get ticket mapping with price breakdown
   * @param {string} ticketId - Ticket ID
   * @returns {Promise<Object>} Ticket details with payment breakdown
   */
  getTicketMapping: async (ticketId) => {
    try {
      const response = await walletAPI.get(`/api/tickets/${ticketId}/mapping`);
      return response.data?.data || {};
    } catch (error) {
      console.error('Error fetching ticket mapping:', error);
      throw error;
    }
  }
};

/**
 * Finance Dashboard
 */
export const FinanceDashboardAPI = {
  /**
   * Get complete dashboard summary
   * @returns {Promise<Object>} Dashboard data
   */
  getDashboardSummary: async () => {
    try {
      const response = await walletAPI.get('/api/finance/dashboard-summary');
      return response.data?.data?.dashboard || {};
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      throw error;
    }
  },

  /**
   * Get financial report by type
   * @param {string} reportType - Report type (DAILY, WEEKLY, MONTHLY, etc.)
   * @returns {Promise<Object>} Financial report
   */
  getReport: async (reportType) => {
    try {
      const response = await walletAPI.get('/api/finance/reports/latest', {
        params: { reportType }
      });
      return response.data?.data?.report || {};
    } catch (error) {
      console.error('Error fetching financial report:', error);
      throw error;
    }
  },

  /**
   * Get pending approvals summary
   * @returns {Promise<Object>} Pending approvals data
   */
  getPendingSummary: async () => {
    try {
      const response = await walletAPI.get('/api/finance/pending-summary');
      return response.data?.data || {};
    } catch (error) {
      console.error('Error fetching pending summary:', error);
      throw error;
    }
  },

  /**
   * Get pending payouts count
   * @returns {Promise<number>} Pending payouts count
   */
  getPendingPayouts: async () => {
    try {
      const response = await walletAPI.get('/api/finance/pending-payouts');
      return response.data?.data?.pendingPayoutsCount || 0;
    } catch (error) {
      console.error('Error fetching pending payouts:', error);
      throw error;
    }
  }
};

export default walletAPI;
